import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeatmapCanvasComponent } from './heatmap-canvas.component';
import { ReplayFrameComponent } from './replay-frame.component';
import type { HeatmapCell, TargetMarker } from '../tracking-demo.models';

/**
 * One screen's click heatmap: a paused frame of that screen (reconstructed from the
 * recorded DOM) with the click heatmap overlaid. Used per session (its own recording)
 * and per product (the newest recording as the background, all sessions' clicks on top).
 *
 * Contain-fit, like the replay: the frame box is sized to the largest viewport-shaped
 * rectangle that fits the card's area (width AND height), so the whole screen is visible
 * without scrolling; the area can go full screen as a unit and the fit follows.
 */
@Component({
  selector: 'app-screen-heatmap',
  standalone: true,
  imports: [CommonModule, HeatmapCanvasComponent, ReplayFrameComponent],
  // Placed in a bounded grid cell: a sized flex column so the fit area has a real height.
  host: { class: 'flex min-h-0 flex-col' },
  template: `
    <div class="flex min-h-0 flex-1 flex-col rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-3">
      <div class="mb-2 flex shrink-0 items-center gap-2">
        <div class="min-w-0 flex-1 truncate text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{{ title }}</div>
        <div class="text-[11px] tabular-nums text-slate-400">{{ clicks }} click{{ clicks === 1 ? '' : 's' }}</div>
        @if (fullscreenSupported) {
          <button type="button" (click)="toggleFullscreen()"
            [title]="isFullscreen() ? 'Exit full screen' : 'Full screen'"
            class="rounded-md border border-[#EAEAEA] dark:border-line px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 dark:text-ink-soft/70 dark:hover:bg-line">
            {{ isFullscreen() ? '⤡' : '⛶' }}
          </button>
        }
      </div>

      <!-- fit area (goes full screen); the box inside keeps the recorded viewport's aspect,
           so frame and ‰ overlay line up -->
      <div #area class="sh-area relative flex min-h-0 flex-1 items-center justify-center">
        <div #box class="relative overflow-hidden rounded-lg bg-slate-900/90" [style.aspectRatio]="aspect">
          @if (src) {
            @defer (on viewport) {
              <app-replay-frame class="absolute inset-0" [src]="src" [atMs]="atMs" />
            } @placeholder {
              <div class="absolute inset-0 grid place-items-center text-xs text-slate-400">Loading screen…</div>
            }
          }
          <app-heatmap-canvas [overlay]="true" [points]="points" />
          <!-- top clicked elements, labelled where their clicks centre -->
          @for (m of markers; track m.label) {
            <div class="pointer-events-none absolute z-10 max-w-[60%] -translate-x-1/2 -translate-y-1/2 truncate rounded-full bg-slate-900/85 px-2 py-0.5 text-[10px] font-medium text-white shadow ring-1 ring-white/20"
                 [style.left.%]="m.x / 10" [style.top.%]="m.y / 10" [title]="m.label + ' · ' + m.count">
              {{ m.label }} · {{ m.count }}
            </div>
          }
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`.sh-area:fullscreen { background: #0f172a; }`],
})
export class ScreenHeatmapComponent implements AfterViewInit, OnDestroy {
  @Input() title = '';
  /** The recording to draw the screen from (session-dom.json.gz URL); null = no background. */
  @Input() src: string | null = null;
  /** Replay offset (ms) at which this screen is on screen, settled. */
  @Input() atMs = 0;
  @Input() points: HeatmapCell[] = [];
  @Input() clicks = 0;
  /** Top clicked elements, each labelled on the frame at its click centroid. */
  @Input() markers: TargetMarker[] = [];

  private _aspect = 16 / 9;
  /** Box aspect (w / h) — the recorded viewport's. */
  @Input() set aspect(value: number) {
    this._aspect = value > 0 ? value : 16 / 9;
    this.fit();
  }
  get aspect(): number {
    return this._aspect;
  }

  readonly isFullscreen = signal(false);
  readonly fullscreenSupported = typeof document !== 'undefined' && !!document.fullscreenEnabled;

  private readonly area = viewChild.required<ElementRef<HTMLDivElement>>('area');
  private readonly box = viewChild.required<ElementRef<HTMLDivElement>>('box');
  private observer: ResizeObserver | null = null;
  private ready = false;
  /** Breathing room around the box in full screen (px). */
  private static readonly FULLSCREEN_INSET = 24;

  private readonly onFullscreenChange = (): void => {
    this.isFullscreen.set(document.fullscreenElement === this.area().nativeElement);
    requestAnimationFrame(() => this.fit());
  };

  ngAfterViewInit(): void {
    this.ready = true;
    this.observer = new ResizeObserver(() => this.fit());
    this.observer.observe(this.area().nativeElement);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    this.fit();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    void this.area().nativeElement.requestFullscreen?.().catch(() => undefined);
  }

  /** Size the box to the largest aspect-true rectangle inside the area (contain-fit). */
  private fit(): void {
    if (!this.ready) return;
    const area = this.area().nativeElement;
    const inset = this.isFullscreen() ? ScreenHeatmapComponent.FULLSCREEN_INSET : 0;
    const availW = area.clientWidth - inset;
    const availH = area.clientHeight - inset;
    if (availW <= 0) return;
    const width = availH > 0 ? Math.min(availW, availH * this._aspect) : availW;
    const box = this.box().nativeElement;
    if (Math.abs((parseFloat(box.style.width) || 0) - width) < 0.5) return; // no sub-pixel churn
    box.style.width = `${width}px`;
  }
}
